import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { getAuthTransport } from '@/lib/api/authTransport';
import { apiFetch } from '@/lib/api/client';
import { runtimeConfig } from '@/lib/config';

import type { FormData } from './docs/schema';
import { DOC_TYPES, isDocType, type DocType } from './docs/types';

/**
 * 안전 관리 일지 데이터 접근 (V506 · DOC-122 · 백엔드 {@code /api/v1/safe/journals}).
 *
 * <p>세 일지가 한 API 를 나눠 쓴다. 권한 키는 서버가 일지 종류로 고른다 — 화면이 키를 보낼 일은 없다.
 */

/** 일지 종류 — 종이 넷 · 표 한 장 둘(V572) · 양식 문서(V574 · docs/types.ts). */
export type JournalType = 'EDUCATION' | 'HEALTH' | 'PATROL' | 'JOINT' | 'RISK' | 'TBM' | DocType;

/** 점검표 · 지적사항을 쓰는 종류 — 순회 · 합동(V571). 서버 SafeJournal.isInspection 과 같다. */
export function isInspection(type: string | null | undefined): boolean {
  return type === 'PATROL' || type === 'JOINT';
}

/** 표 한 장으로 쓰는 종류(V572 · DOC-136) — 종이(JournalPaper) 대신 RiskSheet · TbmSheet 가 그린다. */
export function isSheetType(type: string | null | undefined): type is 'RISK' | 'TBM' | DocType {
  return type === 'RISK' || type === 'TBM' || isDocType(type);
}

/** 교육구분 대분류(V527). 소분류와 법정 교육내용은 서버 카탈로그({@link useEducationCatalog})에 있다. */
export type EducationKind =
  | 'WORKER'
  | 'SUPERVISOR'
  | 'SPECIAL_FORM'
  | 'SPECIAL'
  | 'MSDS'
  | 'OTHER'
  | 'CRISIS'
  | 'HARASSMENT'
  | 'DISABILITY';

export type FindingStatus = 'OPEN' | 'DONE';

export type PatrolKind = 'HEAD' | 'SAFE';

/** GOOD 양호 · ACTION 미흡 · NA 해당없음(V532). */
export type CheckResult = 'GOOD' | 'ACTION' | 'NA';

/** 종류마다 이름 · 주소 — 사이드바(`components/Sidebar`)와 서버(SafeJournalController)와 같은 값이다. */
export const JOURNAL_TYPES: Record<JournalType, { label: string; path: string }> = {
  EDUCATION: { label: '안전교육일지', path: '/education' },
  HEALTH: { label: '안전보건일지', path: '/health' },
  PATROL: { label: '순회점검일지', path: '/patrol' },
  JOINT: { label: '합동점검일지', path: '/joint' },
  RISK: { label: '위험성평가', path: '/risk' },
  TBM: { label: 'TBM 일지', path: '/tbm' },
  ...DOC_TYPES,
};

/**
 * 교육구분 대분류 — 산업안전보건법 시행규칙 별표 5 의 교육대상. 서버 CHECK(V527) · 카탈로그와 한 벌이다.
 * 앱도 이 목록을 쓴다 — 카탈로그를 못 받아도 대분류는 고를 수 있게 여기 둔다.
 */
export const EDUCATION_KINDS: Array<{ value: EducationKind; label: string }> = [
  { value: 'WORKER', label: '근로자 안전보건교육' },
  { value: 'SUPERVISOR', label: '관리감독자 안전보건교육' },
  { value: 'SPECIAL_FORM', label: '특수형태근로종사자 안전보건교육' },
  { value: 'SPECIAL', label: '특별교육 대상 작업별 교육' },
  { value: 'MSDS', label: '물질안전보건자료 교육' },
  { value: 'CRISIS', label: '위기상황발생 대응 매뉴얼 숙지교육' },
  { value: 'HARASSMENT', label: '성희롱예방교육' },
  { value: 'DISABILITY', label: '장애인인식개선교육' },
  { value: 'OTHER', label: '기타' },
];

export function educationKindLabel(kind: string | null | undefined): string {
  return EDUCATION_KINDS.find((one) => one.value === kind)?.label ?? '';
}

/** 교육 갈래 카탈로그 — 서버 {@code SafeEducationCatalog}. 소분류마다 「○ …」 문구 줄이 있다. */
/**
 * 교육 하나(DOC-122 §16) — 회사 양식의 갈래. {@code content} 는 교육 내용 칸에 들어갈 줄이고 첫 줄이 근거 조항이다.
 * hours · target · instructor · material 은 새 일지의 기본값, signers 는 결재 칸 두 이름, box 는 교육 구분 칸 번호(1~6).
 * 재시작 전 서버는 이 칸들을 안 보낸다 — 모두 없을 수 있다.
 */
export type EducationSub = {
  code: string;
  label: string;
  short?: string | null;
  content: string[];
  box?: number | null;
  boxLabel?: string | null;
  hours?: number | null;
  signers?: string[] | null;
  target?: string | null;
  instructor?: string | null;
  material?: string | null;
};

/** 교육 종류 고르개의 한 줄 — sub 가 비면 그 대분류의 소분류(특별 39종)를 다시 고른다. */
export type EducationMenuItem = { code: string; label: string; kind: EducationKind; sub: string | null };

export type EducationCatalog = {
  source: string;
  /** 양식의 교육 구분 칸 여섯 — 6 은 기타(고른 종류의 boxLabel 이 들어간다). */
  boxes?: string[];
  menu?: EducationMenuItem[];
  kinds: Array<{
    code: EducationKind;
    label: string;
    subs: EducationSub[];
  }>;
};

/**
 * 법 조문 위에 [양식 편집] 으로 고친 값이 덮여 온다(V540). 양식을 저장하면 이 질의를 다시 받는다
 * ({@link useEducationTemplateWrite}). 서버가 아직 옛 판이면(404) 비어 있고 대분류만 고른다.
 */
export function useEducationCatalog(enabled = true) {
  return useQuery({
    queryKey: ['safe-journals', 'education-catalog'],
    queryFn: () => apiFetch<EducationCatalog>(`${BASE}/education-catalog`),
    enabled,
    staleTime: Infinity,
    retry: false,
  });
}

/**
 * 교육의 법적 근거 — 교육 내용 첫 줄(근거 조항 줄)에서 읽는다(DOC-122 §26).
 * 「* 산업안전보건법령에 관한 사항(제29조제1항), …」 → 「산업안전보건법 제29조제1항」,
 * 「* 남녀고용평등과 … 법률 (제13조)」 → 「남녀고용평등과 … 법률 제13조」, 「* 산업안전보건법 제114조제3항에 따른 …」 → 그 조항.
 */
export function educationBasis(sub: EducationSub | null | undefined): string | null {
  const first = sub?.content?.[0]?.trim();
  if (!first) return null;
  const act = /산업안전보건법령에 관한 사항\((제[^)]+)\)/.exec(first);
  if (act) return `산업안전보건법 ${act[1]}`;
  const direct = /(산업안전보건법 제\d+조(?:의\d+)?(?:제\d+항)?)/.exec(first);
  if (direct) return direct[1]!;
  const other = /^\*\s*(.+?)\s*\((제[^)]+)\)/.exec(first);
  if (other) return `${other[1]} ${other[2]}`;
  return null;
}

/** 고르개에 보이는 이름 — 괄호 안 설명(법 조항 · 작업 범위)은 뺀다(사용자 2026-11-04 「괄호 안 내용 같은 불필요한 설명 빼」). */
export function plainLabel(label: string): string {
  return label.replace(/\s*\([^()]*\)/g, '').trim() || label;
}

export function educationSubsOf(catalog: EducationCatalog | undefined, kind: string | null | undefined) {
  return catalog?.kinds.find((one) => one.code === kind)?.subs ?? [];
}

/** 「대분류 · 소분류」. 카탈로그가 없거나 소분류를 모르면 대분류만. */
export function educationLabel(
  catalog: EducationCatalog | undefined,
  kind: string | null | undefined,
  subKind: string | null | undefined,
): string {
  const major = educationKindLabel(kind);
  const sub = educationSubsOf(catalog, kind).find((one) => one.code === subKind)?.label;
  return sub ? `${major} · ${sub}` : major;
}

export type Attendee = {
  id?: string | null;
  name: string;
  affiliation: string | null;
  jobType: string | null;
  hours: number | null;
  note: string | null;
};

export type HealthItem = {
  id?: string | null;
  workContent: string | null;
  location: string | null;
  hazard: string | null;
  measure: string | null;
  checked: boolean;
  note: string | null;
};

export type Finding = {
  id?: string | null;
  location: string | null;
  finding: string;
  action: string | null;
  responsible: string | null;
  dueDate: string | null;
  status: FindingStatus;
  doneDate: string | null;
};

/** 안전보건일지의 소장 · 안전 Patrol 한 줄(V527). 점검자는 안전 Patrol 만. */
export type PatrolNote = {
  id?: string | null;
  patrolKind: PatrolKind;
  inspector: string | null;
  instruction: string | null;
  result: string | null;
};

export type Activity = {
  id?: string | null;
  activity: string | null;
  meeting: string | null;
};

export type Facility = {
  id?: string | null;
  facilityKind: string | null;
  location: string | null;
  workers: number | null;
  detail: string | null;
};

/** 순회점검 점검표 한 줄(V527). 결과가 null 이면 아직 안 본 항목. */
export type Check = {
  id?: string | null;
  category: string | null;
  item: string;
  result: CheckResult | null;
};

export type JournalPhoto = {
  id: string;
  fileName: string | null;
  shotAt: string | null;
  caption: string | null;
  /** 사진대지 자리(V533) — TBM_AM · TBM_PM. 비면 일반 사진. 재시작 전 서버는 안 보낸다. */
  slot?: PhotoSlot | null;
  sortOrder: number;
  url: string | null;
};

export type PhotoSlot = 'TBM_AM' | 'TBM_PM';

/** 안전보건일지의 T.B.M 장과 사진대지의 위치(V533). 인원은 명. */
export type Tbm = {
  notice: string | null;
  accidentCase: string | null;
  vendorCaution: string | null;
  workforceOwn: number | null;
  workforceVendor: number | null;
  amOwn: number | null;
  amVendor: number | null;
  pmOwn: number | null;
  pmVendor: number | null;
  absentAm: string | null;
  absentPm: string | null;
  amLocation: string | null;
  pmLocation: string | null;
};

export function blankTbm(): Tbm {
  return {
    notice: null,
    accidentCase: null,
    vendorCaution: null,
    workforceOwn: null,
    workforceVendor: null,
    amOwn: null,
    amVendor: null,
    pmOwn: null,
    pmVendor: null,
    absentAm: null,
    absentPm: null,
    amLocation: null,
    pmLocation: null,
  };
}

export type JournalSummary = {
  id: string;
  journalType: JournalType;
  siteId: string | null;
  siteName: string | null;
  journalDate: string;
  title: string | null;
  educationKind: EducationKind | null;
  /** V527 — 재시작 전 서버는 이 칸을 안 보낸다. */
  educationSubKind?: string | null;
  startTime: string | null;
  authorName: string | null;
  place: string | null;
  inspector: string | null;
  /** V528 — 재시작 전 서버는 안 보낸다. */
  target?: string | null;
  headcount?: number | null;
  content: string | null;
  lineCount: number;
  openCount: number;
  photoCount: number;
  updatedAt: string | null;
  /** 결재란 「현장소장」 승인(V548) — 목록의 「승인」 칸. 승인 전 · 옛 서버면 null · 없음. */
  headApprovedAt?: string | null;
  headApproverName?: string | null;
  /** 결재란의 다른 칸(안전관리자 · 담당 · 관리감독자) 승인(V549). */
  managerApprovedAt?: string | null;
  managerApproverName?: string | null;
  /** TBM 일지(V572) — 재시작 전 서버는 안 보낸다. */
  vendorName?: string | null;
  workItems?: string[];
  /** 기존 데이터에서 옮긴 일지(V612) — 옛 메뉴 · 옛 번호. 누르면 편집 창 대신 원본 한 장이 뜬다. 재시작 전 서버는 안 보낸다. */
  legacySource?: string | null;
  legacyCode?: string | null;
  /** 목록 맨 위에 고정한 예시(V614). 지우면 사라진다. 재시작 전 서버는 안 보낸다. */
  pinned?: boolean;
};

/** 결재란 승인의 칸 — HEAD 는 이름에 「소장」 이 든 칸, MANAGER 는 나머지 한 칸. */
export type ApprovalRole = 'HEAD' | 'MANAGER';

export type Journal = {
  id: string;
  journalType: JournalType;
  siteId: string | null;
  siteName: string | null;
  journalDate: string;
  title: string | null;
  weather: string | null;
  temperature: string | null;
  authorId: string | null;
  authorName: string | null;
  educationKind: EducationKind | null;
  educationSubKind: string | null;
  /** 교육자료 및 보조기기(V537). */
  educationMaterial: string | null;
  startTime: string | null;
  endTime: string | null;
  educationHours: number | null;
  instructor: string | null;
  /** 교육 대상(V528). */
  target: string | null;
  /** 교육 인원(V528). */
  headcount: number | null;
  place: string | null;
  inspector: string | null;
  workforce: number | null;
  content: string | null;
  specialNote: string | null;
  attendees: Attendee[];
  healthItems: HealthItem[];
  findings: Finding[];
  patrolNotes: PatrolNote[];
  activities: Activity[];
  facilities: Facility[];
  checks: Check[];
  /** 안전보건일지 T.B.M 장(V533). 안 적은 일지는 null — 저장 본문에서 null 이면 서버가 건드리지 않는다. */
  tbm: Tbm | null;
  /**
   * 안전보건일지 1장 표들의 줄 수(V551) — 종이의 [＋] · [−]. 저장 본문에 실어 보내고, 비면 서버가 저장된 것을 둔다.
   * 옛 서버 · 저장한 적 없는 일지는 비어 있다(양식의 줄 수).
   */
  rowLayout?: Record<string, number> | null;
  photos: JournalPhoto[];
  createdAt: string | null;
  updatedAt: string | null;
  /** 결재란 「현장소장」 승인(V548). 승인 전 · 옛 서버면 null · 없음. 저장 본문에는 싣지 않는다. */
  headApproval?: HeadApproval | null;
  /** 결재란의 다른 칸(안전관리자 · 담당 · 관리감독자) 승인(V549). */
  managerApproval?: HeadApproval | null;
  /** 결재 요청(V550) — 칸마다 지정한 사람과 할일 · 앱 푸시 스위치. 옛 서버면 없음. 저장 본문에 실린다. */
  signRoute?: SignRoute | null;
  /** TBM 일지(V572) — 작성업체 · 작업종류(「공종 - 작업」) · 회의 참석자. */
  vendorName?: string | null;
  workItems?: string[];
  attendeeNames?: string | null;
  /** 위험성평가 줄(V572). */
  riskItems?: RiskItem[];
  /** 양식 문서의 값(V574) — docs/schema.ts 의 FormData. */
  formData?: FormData | null;
};

/** 위험성평가 한 줄(V572). 가능성 · 중대성 1~3, 위험성 = 곱. keyPoint 면 TBM 체크리스트로 따라간다. */
export type RiskItem = {
  id?: string | null;
  journalId?: string | null;
  vendorName: string | null;
  workType: string | null;
  task: string | null;
  hazard: string;
  likelihood: number | null;
  severity: number | null;
  measure: string | null;
  owner: string | null;
  dueDate: string | null;
  keyPoint: boolean;
};

/** 위험성 등급 — 서버 SafeJournalDocService.riskGrade 와 같은 경계. */
export function riskLevel(item: Pick<RiskItem, 'likelihood' | 'severity'>): { score: number; label: string; tone: string } | null {
  if (!item.likelihood || !item.severity) return null;
  const score = item.likelihood * item.severity;
  return score >= 6 ? { score, label: '높음', tone: 'badge-red' } : score >= 3 ? { score, label: '보통', tone: 'badge-yellow' } : { score, label: '낮음', tone: 'badge-green' };
}

/** 「공종 - 작업」 — TBM 작업종류 한 줄. */
export function workLabel(item: Pick<RiskItem, 'workType' | 'task'>): string {
  return [item.workType, item.task].filter((one) => one && one.trim()).join(' - ');
}

/** 그 현장 위험성평가의 줄 모음(같은 줄은 최근 평가 하나) — TBM 일지의 작업종류 · 체크리스트. */
export function useSiteRiskItems(siteId: string | null) {
  return useQuery({
    queryKey: ['safe-site', siteId, 'risk-items'],
    queryFn: () => apiFetch<RiskItem[]>(`/v1/safe/sites/${siteId}/risk-items`),
    enabled: !!siteId && siteId !== HQ,
  });
}

/**
 * 결재 요청(V550). 사람은 인력 id(주소록)로 고른다 — 서버가 계정을 찾는다. 차례는 다른 칸(안전관리자 · 담당 ·
 * 관리감독자) → 소장 칸이고, 저장하면 차례가 온 사람에게 할일 · 앱 푸시가 간다(같은 사람에게 다시 보내지 않는다).
 */
export type SignRoute = {
  managerPersonnelId: string | null;
  managerName: string | null;
  headPersonnelId: string | null;
  headName: string | null;
  notifyTodo: boolean;
  notifyPush: boolean;
};

export function blankSignRoute(): SignRoute {
  return { managerPersonnelId: null, managerName: null, headPersonnelId: null, headName: null, notifyTodo: true, notifyPush: true };
}

/** signImage — 승인한 순간의 전자결재 개인 서명(PNG data URL). 등록하지 않았으면 null — 이름만 찍는다. */
export type HeadApproval = { approvedAt: string; approverName: string | null; signImage: string | null };

/**
 * 저장 본문. 줄은 통째로 갈아 끼운다(서버가 빈 줄을 버린다). V527 의 넷(patrolNotes · activities · facilities ·
 * checks)은 빠지면 서버가 그 표를 건드리지 않는다 — 비우려면 빈 목록을 보낸다.
 */
export type JournalInput = Omit<
  Journal,
  'id' | 'siteName' | 'authorId' | 'photos' | 'createdAt' | 'updatedAt' | 'headApproval' | 'managerApproval'
>;

/** 응답에서 V527 · V528 · V533 칸을 받는다 — 재시작 전 서버는 안 보낸다(빈 목록 · null 로 읽는다). */
export function serverAddedFields(journal: Journal): Pick<
  Journal,
  | 'educationSubKind'
  | 'educationMaterial'
  | 'target'
  | 'headcount'
  | 'patrolNotes'
  | 'activities'
  | 'facilities'
  | 'checks'
  | 'tbm'
  | 'rowLayout'
  | 'vendorName'
  | 'workItems'
  | 'attendeeNames'
  | 'riskItems'
  | 'formData'
> {
  return {
    formData: journal.formData ?? null,
    vendorName: journal.vendorName ?? null,
    workItems: journal.workItems ?? [],
    attendeeNames: journal.attendeeNames ?? null,
    riskItems: journal.riskItems ?? [],
    tbm: journal.tbm ?? null,
    rowLayout: journal.rowLayout ?? null,
    educationSubKind: journal.educationSubKind ?? null,
    educationMaterial: journal.educationMaterial ?? null,
    target: journal.target ?? null,
    headcount: journal.headcount ?? null,
    patrolNotes: journal.patrolNotes ?? [],
    activities: journal.activities ?? [],
    facilities: journal.facilities ?? [],
    checks: journal.checks ?? [],
  };
}

/** 목록의 현장 고르개 — 빈 글자 = 전체 · `HQ` = 본사 · 그 밖은 현장 id. */
export const HQ = 'HQ';

const BASE = '/v1/safe/journals';

export const safeJournalKeys = {
  all: ['safe-journals'] as const,
  list: (type: JournalType, site: string, from: string, to: string) => ['safe-journals', 'list', type, site, from, to] as const,
  one: (id: string | null) => ['safe-journals', 'one', id] as const,
};

export function useSafeJournals(type: JournalType, site: string, from: string, to: string) {
  return useQuery({
    queryKey: safeJournalKeys.list(type, site, from, to),
    queryFn: () => {
      const query = new URLSearchParams({ type });
      if (site) query.set('site', site);
      if (from) query.set('from', from);
      if (to) query.set('to', to);
      return apiFetch<JournalSummary[]>(`${BASE}?${query.toString()}`);
    },
  });
}

export function useSafeJournal(id: string | null) {
  return useQuery({
    queryKey: safeJournalKeys.one(id),
    queryFn: () => apiFetch<Journal>(`${BASE}/${id}`),
    enabled: !!id,
  });
}

/**
 * 그 현장(본사는 {@link HQ})·그 종류에서 이 날짜까지의 가장 최근 일지. 없으면 null.
 * 따로 API 를 두지 않고 목록(날짜 내림차순)의 첫 줄을 연다.
 */
export async function fetchLatestSafeJournal(type: JournalType, site: string, upTo: string): Promise<Journal | null> {
  const query = new URLSearchParams({ type, site, to: upTo });
  const rows = await apiFetch<JournalSummary[]>(`${BASE}?${query.toString()}`);
  const latest = rows[0];
  return latest ? apiFetch<Journal>(`${BASE}/${latest.id}`) : null;
}

export function addSafeJournalPhoto(
  journalId: string,
  body: { objectKey: string; fileName: string; shotAt: string | null; caption?: string | null; slot?: PhotoSlot | null },
): Promise<Journal> {
  return apiFetch<Journal>(`${BASE}/${journalId}/photos`, { method: 'POST', body });
}

/**
 * 빈 날씨 칸을 미리 채울 값 — 현장 좌표(본사는 홈 날씨의 기본 지점)의 오늘 날씨. 지난 날짜는 서버가
 * {@code available=false} 로 답한다. 비워 저장해도 서버가 같은 값을 찍는다 — 이건 미리 보여 줄 뿐이다.
 */
export function useSafeJournalWeather(type: JournalType, site: string, date: string, enabled: boolean) {
  return useQuery({
    queryKey: ['safe-journals', 'weather', site, date],
    queryFn: () =>
      apiFetch<{ available: boolean; weather: string | null; temperature: string | null }>(
        `${BASE}/weather?${new URLSearchParams({ type, site, date }).toString()}`,
      ),
    enabled: enabled && !!site && !!date,
    // 서버 날씨 캐시와 같은 10분. 못 받은 것도 답이라 다시 묻지 않는다.
    staleTime: 10 * 60 * 1000,
    retry: false,
  });
}

export function useSafeJournalWrite() {
  const queryClient = useQueryClient();
  return useMutation({
    // 삭제만 본문이 없다. 반환 타입을 합집합으로 못 박지 않으면 react-query 가 void 로 좁힌다.
    mutationFn: ({ id, body }: { id?: string | null; body: JournalInput | null }): Promise<Journal | void> => {
      if (body === null) return apiFetch<void>(`${BASE}/${id}`, { method: 'DELETE' });
      if (id) return apiFetch<Journal>(`${BASE}/${id}`, { method: 'PUT', body });
      return apiFetch<Journal>(BASE, { method: 'POST', body });
    },
    onSuccess: (data) => {
      if (data) queryClient.setQueryData(safeJournalKeys.one(data.id), data);
      return queryClient.invalidateQueries({ queryKey: [...safeJournalKeys.all, 'list'] });
    },
  });
}

export type PhotoAction =
  | { kind: 'add'; body: { objectKey: string; fileName: string; shotAt: string | null } }
  | { kind: 'caption'; photoId: string; body: { caption: string | null } }
  | { kind: 'remove'; photoId: string };

export function useSafeJournalPhotoWrite(journalId: string | null) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (action: PhotoAction) => {
      if (action.kind === 'add') return addSafeJournalPhoto(journalId ?? '', action.body);
      if (action.kind === 'caption') {
        return apiFetch<Journal>(`${BASE}/${journalId}/photos/${action.photoId}`, { method: 'PUT', body: action.body });
      }
      return apiFetch<Journal>(`${BASE}/${journalId}/photos/${action.photoId}`, { method: 'DELETE' });
    },
    onSuccess: (data) => {
      queryClient.setQueryData(safeJournalKeys.one(journalId), data);
      return queryClient.invalidateQueries({ queryKey: [...safeJournalKeys.all, 'list'] });
    },
  });
}

/**
 * 결재란 「현장소장」 승인 · 취소(V548). 승인하면 누른 사람의 이름과 전자결재 개인 서명이 찍힌다.
 * 고쳐 저장하면 승인은 풀린다(서버).
 */
export function useHeadApproval(journalId: string | null) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ target, approve, role = 'HEAD' }: { target: string; approve: boolean; role?: ApprovalRole }) =>
      apiFetch<Journal>(`${BASE}/${target}/head-approval?role=${role}`, { method: approve ? 'POST' : 'DELETE' }),
    onSuccess: (data) => {
      queryClient.setQueryData(safeJournalKeys.one(data.id), data);
      if (journalId && journalId !== data.id) queryClient.setQueryData(safeJournalKeys.one(journalId), data);
      return queryClient.invalidateQueries({ queryKey: [...safeJournalKeys.all, 'list'] });
    },
  });
}

/** 저장소에 파일을 올리고 키를 돌려준다. 파일 바이트는 API 서버를 지나지 않는다. */
export async function uploadSafeJournalFile(journalType: JournalType, file: Blob, fileName: string): Promise<string> {
  const { key, url } = await apiFetch<{ key: string; url: string }>(`${BASE}/uploads`, {
    method: 'POST',
    body: { journalType, fileName },
  });
  const response = await fetch(url, {
    method: 'PUT',
    body: file,
    headers: { 'Content-Type': file.type || 'application/octet-stream' },
  });
  if (!response.ok) throw new Error(`파일을 저장소에 올리지 못했습니다. (HTTP ${response.status})`);
  return key;
}

/**
 * PDF 내려받기 — 보던 탭 그대로 받는다. {@link apiFetch} 는 봉투를 풀어 JSON 을 돌려주므로 파일이
 * 통과하지 못한다. 인증만은 같은 자리({@link getAuthTransport})에서 가져온다.
 */
export async function downloadSafeJournal(id: string, fallbackName: string): Promise<void> {
  const transport = getAuthTransport();
  const response = await fetch(`${runtimeConfig().apiBaseUrl}${BASE}/${id}/export`, {
    credentials: transport.credentials,
    headers: transport.authHeaders({ unsafe: false }),
  });
  if (!response.ok) throw new Error(`파일을 만들지 못했습니다. (HTTP ${response.status})`);

  const blob = await response.blob();
  // 파일명은 서버가 Content-Disposition 에 RFC 5987 로 실어 보낸다. 못 읽으면 기본 이름을 쓴다.
  const disposition = response.headers.get('Content-Disposition') ?? '';
  const encoded = /filename\*=UTF-8''([^;]+)/i.exec(disposition)?.[1];
  const fileName = encoded ? decodeURIComponent(encoded) : `${fallbackName}.pdf`;

  const link = document.createElement('a');
  link.href = URL.createObjectURL(blob);
  link.download = fileName;
  link.click();
  URL.revokeObjectURL(link.href);
}

// ── 순회점검 양식 (V535 · DOC-122 §14) ───────────────────────────────────────

export type PatrolTemplateItem = { category: string | null; item: string };

/**
 * 순회점검일지 종이의 머리 글(V540 PATROL) — 서버 SafePatrolTemplateService.DEFAULT_HEADER 와 같은 기본값.
 * signers 는 1~2개, resultLabels 는 차례대로 양호 · 미흡 · 해당없음 자리 셋. subtitle 은 빈 글이면 부제 없음.
 */
export type PatrolHeader = {
  title: string;
  subtitle: string;
  signers: string[];
  categoryLabel: string;
  itemLabel: string;
  resultLabels: string[];
  noteLabel: string;
};

export const DEFAULT_PATROL_HEADER: PatrolHeader = {
  title: '순회점검일지',
  subtitle: '(2일에 1회)',
  signers: ['소장'],
  categoryLabel: '구분',
  itemLabel: '현 장 순 회 점 검 내 용',
  resultLabels: ['양 호', '미 흡', '해당없음'],
  noteLabel: '특이사항 및 조치사항',
};

/** 그 종류 종이의 머리 — 합동점검일지는 순회점검 양식에 제목 · 주기 · 내용 칸 이름만 바꾼다(서버 headerFor 와 같다). */
export function headerFor(type: string, header: PatrolHeader): PatrolHeader {
  if (type !== 'JOINT') return header;
  return { ...header, title: '합동점검일지', subtitle: '(2개월에 1회)', itemLabel: '합 동 점 검 내 용' };
}

/** 양식 — 머리 글 · 점검 항목 회사 공통 한 벌. {@code canEdit} 은 안전보건팀 · 최고관리자. 옛 서버는 header 를 안 보낸다. */
export type PatrolTemplate = { canEdit: boolean; header?: PatrolHeader | null; items: PatrolTemplateItem[] };

const TEMPLATE = '/v1/safe/patrol-template';

/** 재시작 전 서버면(404) 비어 있다 — 새 일지는 화면 상수(patrolChecklist.ts)로 시작한다. */
export function usePatrolTemplate(enabled = true) {
  return useQuery({
    queryKey: ['safe-journals', 'patrol-template'],
    queryFn: () => apiFetch<PatrolTemplate>(TEMPLATE),
    enabled,
    retry: false,
  });
}

export function usePatrolTemplateWrite() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: { header: PatrolHeader; items: PatrolTemplateItem[] }) =>
      apiFetch<PatrolTemplate>(TEMPLATE, { method: 'PUT', body }),
    onSuccess: (data) => queryClient.setQueryData(['safe-journals', 'patrol-template'], data),
  });
}

// ── 안전교육 종류별 양식 (V540 · DOC-122 §20) ────────────────────────────────

/** 교육 한 종류의 양식. defaults 는 법정 기본값([기본값으로]), current 는 지금 새 일지가 따르는 것. */
export type EducationTemplate = {
  canEdit: boolean;
  kind: string;
  sub: string;
  defaults: EducationSub;
  current: EducationSub;
  customized: boolean;
};

export type EducationTemplateSave = {
  signers: string[];
  boxLabel: string | null;
  hours: number | null;
  target: string | null;
  instructor: string | null;
  material: string | null;
  content: string[];
};

const EDU_TEMPLATE = '/v1/safe/education-templates';

export function useEducationTemplate(kind: string | null, sub: string | null) {
  return useQuery({
    queryKey: ['safe-journals', 'education-template', kind, sub],
    queryFn: () => apiFetch<EducationTemplate>(`${EDU_TEMPLATE}/${kind}/${sub}`),
    enabled: !!kind && !!sub,
    retry: false,
  });
}

/** 저장 · [기본값으로](body 없음 = DELETE). 끝나면 고르개 · 새 일지가 쓰는 카탈로그를 다시 받는다. */
export function useEducationTemplateWrite() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ kind, sub, body }: { kind: string; sub: string; body: EducationTemplateSave | null }) =>
      apiFetch<EducationTemplate>(
        `${EDU_TEMPLATE}/${kind}/${sub}`,
        body ? { method: 'PUT', body } : { method: 'DELETE' },
      ),
    onSuccess: (data) => {
      queryClient.setQueryData(['safe-journals', 'education-template', data.kind, data.sub], data);
      return queryClient.invalidateQueries({ queryKey: ['safe-journals', 'education-catalog'] });
    },
  });
}
